import { defineStore } from "pinia";
import { useAxios } from "@/composables/api.js";
import { useCandidateStore } from "@/stores/candidate";
import { useSpeedTestStore } from "./speedtest";
import { useCompatibilityCheckStore } from "./compatibilityCheck";
import { fetchTokenQuery, logout } from '@/queries/user.query';
import { ref } from "vue";
import mitt from "mitt";
import axios from "axios";


export const useCloudClientStore = defineStore("cloud-client", () => {
  const emitter = mitt();

  const compatibilityStore = useCompatibilityCheckStore();
  const candidateStore = useCandidateStore();
  const speedtest = useSpeedTestStore();
  const serverURL = import.meta.env.VITE_MESSAGE_HUB_URL;

  let participant_id = null;
  let campaign_creation_date = null;
  let candidate_id = null;
  let session_id = null;
  let mode = null;
  let yuzu_version = 1;
  let socket = null;
  let pixelStreamingURL = ref(null);
  let speedtestURL = null;
  let pingInterval = null;
  let response_stream = null;
  let hubAxios = null;

  function init(new_participant_id, cmp_version, campaign_creation_date_in, campaign_mode) {
    participant_id = new_participant_id;
    candidate_id = candidateStore.candidate.id;
    yuzu_version = cmp_version;
    pixelStreamingURL.value = null;
    campaign_creation_date = campaign_creation_date_in;
    mode = campaign_mode;
  }

  function canStart() {
    return participant_id != null && candidate_id != null;
  }

  async function startSession() {
    try {
      const { data } = await useAxios.post(
        `/candidates/me/assessments/${participant_id}`
      );
      if (!data || !data.session_id) {
        return false;
      }
      session_id = data.session_id;

      return true;
    } catch (error) {
      return false;
    }
  }

  async function requestReservation() {
    if (!canStart() || session_id == null) {
        emitter.emit("onCancelSpeedtest");
      return {available: false, maintenance: false, error: true, started: false};
    }
    if (!hubAxios) {
      const tokenObject = await fetchTokenQuery()
      hubAxios = axios.create({
        baseURL: import.meta.env.VITE_MESSAGE_HUB_URL,
        headers: {
          "Authorization": `Bearer ${tokenObject && tokenObject.token ? tokenObject.token : "none"}`,
        },
      });
  }
    try {
      response_stream = `${session_id}-reservation-${new Date().getTime()}`;
      const response = await hubAxios.post(`${serverURL}/api/v1/candidates/operator`, {
        type: "reserve_streaming_session",
        candidate_id: participant_id,
        session_id,
        version: yuzu_version,
        ip: "",
        response_stream,
        candidate_email: candidateStore.candidate.personal_information.email,
        filters: [],
        codec: await compatibilityStore.av1Supported() ?  "AV1" : "H264"
      });
      if (response.status !== 200) {
        console.error("Error reserving streaming session:", response.data);
        emitter.emit("onCancelSpeedtest");
        return {available: false, maintenance: false, error: true, started: false};
      }
      if (!response.data || !response.data.type) {
        emitter.emit("onCancelSpeedtest");
        return {available: false, maintenance: false, error: true, started: false};
      }
      switch (response.data.type) {
        case "no_response_yet": {
          return await pollSpeedTestURL(response_stream);
        }
        case "reservation_created": {
          if (response.data.speedtest_url) {
            speedtestURL = response.data.speedtest_url;
            emitter.emit("onSpeedTestURLAvailable");
          }
          return {available: true, maintenance: false, error: false, started: false};
        }
        case "session_already_started": {
          emitter.emit("onCancelSpeedtest");
          return {available: true, maintenance: false, error: false, started: true};
        }
        case "out_of_support": {
          emitter.emit("onCancelSpeedtest");
          return {available: false, maintenance: false, error: false, started: false, reason: "out_of_support"};
        }
        default: {
          emitter.emit("onSessionNotAvailable", {maintenance: false, started: false, error: true});
          emitter.emit("onCancelSpeedtest");
          return {available: false, maintenance: false, error: true, started: false};
        }
      }
    } catch (e) {
      console.error("Error reserving streaming session:", e);
      emitter.emit("onCancelSpeedtest");
      return {available: false, maintenance: false, error: true, started: false};
    }
  }

  function pollSpeedTestURL(response_stream, pollIntervalMs = 1000) {
    if (!response_stream) return Promise.resolve();
    return new Promise((resolve, reject) => {
      const poll = async () => {
        try {
          const response = await hubAxios.get(`${serverURL}/api/v1/candidates/${response_stream}/get-message`);
          if (response.status !== 200) {
            console.error("Error reserving streaming session:", response.data);
            emitter.emit("onCancelSpeedtest");
            resolve({available: false, maintenance: false, error: true, started: false});
            return;
          }
          if (!response.data || !response.data.type) {
            emitter.emit("onCancelSpeedtest");
            resolve({available: false, maintenance: false, error: true, started: false});
            return;
          }
          switch (response.data.type) {
            case "no_response_yet": {
              setTimeout(poll, pollIntervalMs);
              return;
            }
            case "reservation_created": {
              if (response.data.speedtest_url) {
                speedtestURL = response.data.speedtest_url;
                emitter.emit("onSpeedTestURLAvailable");
              }
              resolve({available: true, maintenance: false, error: false, started: false});
              return;
            }
            case "session_already_started": {
              emitter.emit("onCancelSpeedtest");
              resolve({available: true, maintenance: false, error: false, started: true});
              return;
            }
            case "out_of_support": {
              emitter.emit("onCancelSpeedtest");
              return {available: false, maintenance: false, error: false, started: false, reason: "out_of_support"};
            }
            default: {
              emitter.emit("onSessionNotAvailable", {maintenance: false, started: false, error: true});
              emitter.emit("onCancelSpeedtest");
              resolve({available: false, maintenance: false, error: true, started: false});
              return;
            }
          }
        } catch (e) {
          console.error("Error reserving streaming session:", e);
          emitter.emit("onCancelSpeedtest");
          resolve({available: false, maintenance: false, error: true, started: false});
        }
      };
      poll();
    });
  }

  async function startCloudSession() {
    try {
      response_stream = `${session_id}-launch-${new Date().getTime()}`;
      const response = await hubAxios.post(`${serverURL}/api/v1/candidates/operator`, {
        type: "launch_streaming_session",
        session_id,
        candidate_id: participant_id,
        participant_id: participant_id,
        candidate_email: candidateStore.candidate.personal_information.email,
        candidate_first_name: candidateStore.candidate.personal_information.first_name,
        candidate_last_name: candidateStore.candidate.personal_information.last_name,
        version: yuzu_version,
        mode: mode,
        user_localisation: navigator.language.split("-")[0],
        response_stream
      });
      if (response.status !== 200) {
        console.error("Error launching streaming session:", response.data);
        emitter.emit("onOtherError");
        return;
      }
      if (!response.data || !response.data.type) {
        console.error("Received malformed response", response.data);
        return;
      }
      switch (response.data.type) {
        case "no_response_yet":
        case "session_started":
          return await pollPixelStreamingURL(response_stream);
        case "session_ready": {
          if (response.data.pixel_streaming_url) {
            onPixelStreamingReady(response.data.pixel_streaming_url);
          }
          return;
        }
        case "no_provider_capacity": {
          emitter.emit("onCapacityError");
          return;
        }
        default: {
          emitter.emit("onOtherError");
        }
      }
    } catch (e) {
      console.error("Error launching streaming session:", e);
      emitter.emit("onOtherError");
    }
  }

  function pollPixelStreamingURL(response_stream, pollIntervalMs = 1000) {
    if (!response_stream) return Promise.resolve();
    return new Promise<void>((resolve, reject) => {
      const poll = async () => {
        try {
          const response = await hubAxios.get(`${serverURL}/api/v1/candidates/${response_stream}/get-message`);
          if (response.status !== 200) {
            emitter.emit("onOtherError");
            console.error("Error polling messages:", response.data);
            resolve();
            return;
          }
          if (!response.data || !response.data.type) {
            console.log('Received malformed response', response.data);
            emitter.emit("onOtherError");
            resolve();
            return;
          }
          switch (response.data.type) {
            case "no_response_yet":
            case "session_started": {
              setTimeout(poll, pollIntervalMs);
              return;
            }
            case "session_ready": {
              if (response.data.pixel_streaming_url) {
                onPixelStreamingReady(response.data.pixel_streaming_url);
              }
              resolve();
              return;
            }
            case "no_provider_capacity": {
              emitter.emit("onCapacityError");
              return;
            }
            default: {
              emitter.emit("onOtherError");
              return;
            }
          }
        } catch (e) {
          console.error("Error launching streaming session:", e);
          emitter.emit("onOtherError");
          resolve();
        }
      };
      poll();
    });
  }

  function onPixelStreamingReady(pixel_streaming_url) {
    pixelStreamingURL.value = "http://" + pixel_streaming_url;
    console.log("pixel streaming ready on " + pixelStreamingURL.value);
  }


  function startPingTest() {
    let pingCount = parseInt(import.meta.env.VITE_SPEEDTEST_PING_COUNT);
    let grace = parseInt(import.meta.env.VITE_SPEEDTEST_GRACE_PING_COUNT);
    pingCount = Number.isNaN(pingCount) ? 10 : pingCount;
    grace = Number.isNaN(grace) ? 2 : grace;
    speedtest.pingTest(speedtestURL, pingCount, grace).then((results) => {
      emitter.emit("onCloudOperatorLatencyUpdated", results.networkPing.value);
    });
  }

  async function startDownloadTest() {
    let duration = parseInt(import.meta.env.VITE_SPEEDTEST_DL_DURATION);
    let thread = parseInt(import.meta.env.VITE_SPEEDTEST_DL_THREAD);
    duration = Number.isNaN(duration) ? 5 : duration;
    thread = Number.isNaN(thread) ? 5 : thread;
    if (speedtestURL == null) {
      emitter.emit("onCloudOperatorBandwidthUpdated", -1);
    } else {
      speedtest
        .bandwidthTest(speedtestURL, duration, thread)
        .then((results) => {
          emitter.emit(
            "onCloudOperatorBandwidthUpdated",
            results.networkBandwidth.value
          );
        });
    }
  }

  async function sendSpeedTestResults(ping, bandwidth, codec = "H264") {
    try {
      response_stream = `${session_id}-speedtest-${new Date().getTime()}`;
      const response = await hubAxios.post(`${serverURL}/api/v1/candidates/operator`, {
        type: "speed_test_results",
        ping: ping,
        bandwidth: bandwidth,
        session_id: session_id,
        response_stream: response_stream,
        codec: codec
      });
      console.log("Speed test results sent:", response.data);
      if (response.status !== 200) {
        console.error("Error retrieving streaming quality:", response.data);
        return "FAILED";
      }
      if (!response.data || !response.data.type) {
        return "FAILED";
      }
      switch (response.data.type) {
        case "speed_test_passed": {
          if (response.data.quality) return response.data.quality;
        }
        default: {
          return "FAILED";
        }
      }
    } catch (e) {
      console.error("Error retrieving streaming quality:", e);
      return "FAILED";
    }
  }

  async function pushHardwareSpecs(hardwareSpecs) {
    try {
      const response = await hubAxios.post(`${serverURL}/api/v1/candidates/operator`, {
        type: "hardware_specs_data",
        session_id: session_id,
        hardware_specs: Object.entries(hardwareSpecs).reduce((acc, [key, spec]) => {
          acc[key] = spec.value;
          return acc;
        }, {}),
      });
    } catch (e) {
      console.error("Error sending hardware specs:", e);
    }
  }

  async function pushSurvey(surveyAnswers) {
    try {
      const response = await hubAxios.post(`${serverURL}/api/v1/candidates/operator`, {
        type: "survey_data",
        session_id: session_id,
        answers: surveyAnswers,
      });
    } catch (e) {
      console.error("Error sending survey data:", e);
    }
  }

  function isV2() {
    return yuzu_version === 2;
  }

  return {
    init,
    canStart,
    startSession,
    sendSpeedTestResults,
    startCloudSession,
    requestReservation,
    pixelStreamingURL,
    speedtestURL,
    startPingTest,
    startDownloadTest,
    emitter,
    pushHardwareSpecs,
    pushSurvey,
    isV2,
  };
});
